import { TagOption } from '@tw/tw-component-library';
import { User } from '../components/container/forms/types/api/graph.types';

export interface MapUsersToSuggestionsOptions {
  valueField?: 'mail' | 'userPrincipalName';
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export const toTagOptions = (value: unknown): TagOption[] => {
  if (value === null || value === undefined || value === '') {
    return [];
  }

  const items = Array.isArray(value) ? value : [value];

  return items.flatMap((item) => {
    if (typeof item === 'string') {
      const trimmed = item.trim();
      return trimmed === ''
        ? []
        : [{ value: trimmed, label: trimmed, disabled: false }];
    }

    if (!isRecord(item) || !('value' in item)) {
      return [];
    }

    const tagValue = String(item.value ?? '').trim();
    if (tagValue === '') {
      return [];
    }

    const label =
      typeof item.label === 'string' && item.label.trim() !== ''
        ? item.label
        : tagValue;

    return [
      {
        value: tagValue,
        label,
        disabled: Boolean(item.disabled),
      },
    ];
  });
};

const MAIL_LIST_SEPARATOR = ', ';

/**
 * Mail addresses selected in a tag picker, as one string for API submit.
 * Tag option objects are reduced to their value, which must already be mail.
 */
export const toMailString = (value: unknown): string | undefined => {
  const mails = toTagOptions(value).flatMap((tag) =>
    tag.value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part !== '')
  );
  const uniqueMails = mails.filter(
    (mail, index) => mails.indexOf(mail) === index
  );

  return uniqueMails.length > 0
    ? uniqueMails.join(MAIL_LIST_SEPARATOR)
    : undefined;
};

/**
 * Restores tag picker chips from a submitted mail string or existing tags.
 */
export const toMailTagOptions = (value: unknown): TagOption[] => {
  if (typeof value === 'string') {
    return toTagOptions(
      value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== '')
    );
  }

  return toTagOptions(value);
};

export const areTagOptionsEqual = (
  left: TagOption[],
  right: TagOption[]
): boolean => {
  if (left === right) {
    return true;
  }

  if (left.length !== right.length) {
    return false;
  }

  return left.every(
    (tag, index) =>
      tag.value === right[index]?.value &&
      tag.label === right[index]?.label &&
      Boolean(tag.disabled) === Boolean(right[index]?.disabled)
  );
};

export const mapUsersToSuggestions = (
  users: User[],
  options?: MapUsersToSuggestionsOptions
): TagOption[] => {
  const valueField = options?.valueField ?? 'userPrincipalName';

  return users.map((user) => {
    const displayName = `${user.givenName ?? ''} ${user.surname ?? ''}`.trim();
    const mailValue = user.mail || user.userPrincipalName;

    return {
      value:
        valueField === 'mail' ? mailValue : user.userPrincipalName,
      label:
        valueField === 'mail'
          ? displayName || user.displayName
          : `${user.givenName ?? ''} ${user.surname ?? ''}`,
      disabled: false,
    };
  });
};
